import { isNonEmpty, ProductTypeEnum, type Security } from '@talos/kyoko';
import Big from 'big.js';

export const EIGHTH_SUFFIX_DIGITS = ['0', '1', '2', '3', '5', '6', '7', '8'] as const;

export const GOVERNMENT_BOND_PRICE_DISPLAY_INCREMENT = '0.001';

const THIRTY_SECONDS_PATTERN = '[0-2]\\d|3[01]';
const SUFFIX_CHAR_PATTERN = '[01235678+]';
const THIRTY_SECONDS_CAPTURE = `(${THIRTY_SECONDS_PATTERN})`;
const COMPLETE_TREASURY_PRICE_PATTERN = new RegExp(`^(\\d+)-${THIRTY_SECONDS_CAPTURE}(${SUFFIX_CHAR_PATTERN})$`);
const SHORTHAND_TREASURY_PRICE_PATTERN = new RegExp(`^(\\d+)-${THIRTY_SECONDS_CAPTURE}$`);

const SUFFIX_CHAR_TO_EIGHTH_INDEX: Record<string, number> = {
  '0': 0,
  '1': 1,
  '2': 2,
  '3': 3,
  '+': 4,
  '5': 4,
  '6': 5,
  '7': 6,
  '8': 7,
};

function formatSuffixChar(eighthIndex: number): string {
  return eighthIndex === 4 ? '+' : EIGHTH_SUFFIX_DIGITS[eighthIndex];
}

export function isGovernmentBondSecurity(security: Pick<Security, 'ProductType'> | undefined): boolean {
  return security?.ProductType === ProductTypeEnum.GovernmentBond;
}

export function formatGovernmentBondPriceIn32nds(price: Big): string {
  const isNegative = price.lt(0);
  const ticks256 = price.abs().times(256).round(0, Big.roundHalfUp);
  const wholePoints = ticks256.div(256).round(0, Big.roundDown);
  const fractional256 = ticks256.minus(wholePoints.times(256));
  const thirtySeconds = fractional256.div(8).round(0, Big.roundDown);
  const thirtySecondsText = thirtySeconds.toFixed().padStart(2, '0');
  const suffixChar = formatSuffixChar(fractional256.mod(8).toNumber());
  const formatted = `${wholePoints.toFixed()}-${thirtySecondsText}${suffixChar}`;

  return isNegative ? `-${formatted}` : formatted;
}

function parseTreasuryPriceParts(value: string): {
  wholePointsText: string;
  thirtySeconds: number;
  suffixChar: string;
} {
  const shorthandMatch = SHORTHAND_TREASURY_PRICE_PATTERN.exec(value);
  if (shorthandMatch != null) {
    return {
      wholePointsText: shorthandMatch[1],
      thirtySeconds: Number(shorthandMatch[2]),
      suffixChar: '0',
    };
  }

  const match = COMPLETE_TREASURY_PRICE_PATTERN.exec(value);
  if (match == null) {
    throw new Error(`Invalid government bond 32nds price: ${value}`);
  }

  return {
    wholePointsText: match[1],
    thirtySeconds: Number(match[2]),
    suffixChar: match[3],
  };
}

export function isCompleteGovernmentBond32ndsPrice(value: string): boolean {
  if (SHORTHAND_TREASURY_PRICE_PATTERN.test(value)) {
    return true;
  }

  if (!COMPLETE_TREASURY_PRICE_PATTERN.test(value)) {
    return false;
  }

  const suffixChar = value.at(-1);
  return suffixChar != null && suffixChar !== '4' && suffixChar !== '9';
}

export function isPositiveCompleteGovernmentBond32ndsPrice(value: string): boolean {
  if (!isCompleteGovernmentBond32ndsPrice(value)) {
    return false;
  }

  try {
    return parseGovernmentBondPriceFrom32nds(value).gt(0);
  } catch {
    return false;
  }
}

export function isValidProgressiveGovernmentBond32ndsPrice(value: string): boolean {
  if (value === '') {
    return true;
  }

  if (value.includes('.')) {
    return false;
  }

  if (!/^[\d+-]+$/.test(value)) {
    return false;
  }

  const dashCount = (value.match(/-/g) ?? []).length;
  if (dashCount > 1) {
    return false;
  }

  if (dashCount === 0) {
    return /^\d+$/.test(value);
  }

  const [whole, afterDash = ''] = value.split('-');
  if (whole === '' || whole.startsWith('+')) {
    return false;
  }

  if (afterDash === '') {
    return true;
  }

  if (afterDash === '+') {
    return /^\d{2}$/.test(whole);
  }

  if (!/^[0-9+]+$/.test(afterDash)) {
    return false;
  }

  if (afterDash.length === 1) {
    const digit = Number(afterDash);
    return Number.isInteger(digit) && digit <= 3;
  }

  if (afterDash.length === 2) {
    const thirtySeconds = Number(afterDash);
    return Number.isInteger(thirtySeconds) && thirtySeconds <= 31;
  }

  if (afterDash.length === 3) {
    const thirtySeconds = Number(afterDash.slice(0, 2));
    const suffixChar = afterDash[2];
    return (
      Number.isInteger(thirtySeconds) &&
      thirtySeconds <= 31 &&
      (suffixChar === '+' ||
        suffixChar === '5' ||
        EIGHTH_SUFFIX_DIGITS.includes(suffixChar as (typeof EIGHTH_SUFFIX_DIGITS)[number]))
    );
  }

  return false;
}

export function filterGovernmentBond32ndsPriceInput(nextValue: string): string {
  let result = '';
  let hasDash = false;
  let hasSuffixModifier = false;

  for (const char of nextValue) {
    if (char >= '0' && char <= '9') {
      result += char;
      continue;
    }

    if (char === '-' && !hasDash && result.length > 0) {
      result += char;
      hasDash = true;
      continue;
    }

    if ((char === '+' || char === '5') && hasDash && !hasSuffixModifier) {
      result += char;
      hasSuffixModifier = true;
    }
  }

  return result;
}

export function parseGovernmentBondPriceFrom32nds(value: string): Big {
  if (!isCompleteGovernmentBond32ndsPrice(value)) {
    throw new Error(`Invalid government bond 32nds price: ${value}`);
  }

  const { wholePointsText, thirtySeconds, suffixChar } = parseTreasuryPriceParts(value);
  const eighthIndex = SUFFIX_CHAR_TO_EIGHTH_INDEX[suffixChar];
  if (eighthIndex == null) {
    throw new Error(`Invalid government bond 32nds price: ${value}`);
  }

  const fractional256 = thirtySeconds * 8 + eighthIndex;

  return Big(wholePointsText).plus(Big(fractional256).div(256));
}

export function canonicalizeGovernmentBond32ndsPrice(value: string): string {
  if (!isPositiveCompleteGovernmentBond32ndsPrice(value)) {
    return value;
  }

  return formatGovernmentBondPriceIn32nds(parseGovernmentBondPriceFrom32nds(value));
}

export function roundGovernmentBondDecimalPriceToNearest32nd(decimalPrice: Big): Big {
  return decimalPrice.times(256).round(0, Big.roundHalfUp).div(256);
}

export function getManualTradeDecimalPrice(
  price: string | undefined,
  security: Security | undefined,
  useGovernmentBond32ndsInput: boolean
): string | undefined {
  if (!isNonEmpty(price)) {
    return undefined;
  }

  if (!isGovernmentBondSecurity(security) || !useGovernmentBond32ndsInput) {
    return price;
  }

  if (!isPositiveCompleteGovernmentBond32ndsPrice(price)) {
    return undefined;
  }

  return parseGovernmentBondPriceFrom32nds(price).toFixed();
}

/** Converts a primed decimal wire price into 32nds for the modify form. Do not call from live price input. */
export function getManualTradeDisplayPrice(
  price: string | undefined,
  productType: ProductTypeEnum | undefined,
  useGovernmentBond32ndsInput: boolean
): string | undefined {
  if (!isNonEmpty(price)) {
    return price;
  }

  if (productType !== ProductTypeEnum.GovernmentBond || !useGovernmentBond32ndsInput) {
    return price;
  }

  if (isCompleteGovernmentBond32ndsPrice(price)) {
    return canonicalizeGovernmentBond32ndsPrice(price);
  }

  try {
    return formatGovernmentBondPriceIn32nds(new Big(price));
  } catch {
    return price;
  }
}

export function usesGovernmentBond32ndsPriceInput(
  security: Pick<Security, 'ProductType'> | undefined,
  enableGovernmentBond32nds: boolean
): boolean {
  return isGovernmentBondSecurity(security) && enableGovernmentBond32nds;
}
